'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { REGEXP_ONLY_DIGITS } from "input-otp"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { navigateUrl } from '@/utils/client-request'
import { usePathname } from 'next/navigation'
import { useForgotPasswordFlow } from '@/lib/commerce/hooks'

import Input from '@/components/elements/Input'
import { Button } from '@/components/elements/Button'
import { CircleCheckBig, MoveLeft, MoveRight, RotateCcw } from 'lucide-react'
import Link from 'next/link'
import { formatImageURL } from '@/utils/util'

export default function Forgot({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const f = useForgotPasswordFlow(vendor)
  const handleKeyDown = (event: any) => { if (event.key === 'Enter') f.sendOTP() }

  return (
    <div className="w-full max-w-md">
      <div className="flex flex-col items-center gap-6">
        {vendor?.logo ? (
          <img src={formatImageURL(vendor?.logo)} alt="logo" className="h-8 max-w-40 w-auto object-contain" />
        ) : vendor?.name ? (
          <div className="text-xl sm:text-2xl font-bold tracking-tight">{vendor?.name}</div>
        ) : null}
        <h1 className="text-center text-foreground text-3xl font-semibold leading-7.5 tracking-[-0.7%]">Нууц үг сэргээх</h1>
      </div>
      <AnimatePresence>
        {(f.step == 3 && f.sentOTP && f.verificationId) ? (
          <div className="mt-14">
            <form onSubmit={f.handleResetPassword} className="flex flex-col gap-y-4">
              <Input label="Шинэ нууц үг" type="password" onChange={(e: any) => f.setPassword(e.target.value)} placeholder="Таны шинэ нууц үг" description="8-аас дээш тэмдэгтэй байх ёстой" />
              <Input label="Шинэ нууц үг давтах" type="password" onChange={(e: any) => f.setPasswordRep(e.target.value)} placeholder="Шинэ нууц үг давтах" />
              <Button type="submit" isLoading={f.loading} variant="outline" className="mt-2 w-full h-10 bg-foreground text-white font-medium! border-foreground hover:bg-foreground/90 hover:text-white transition-all" loadingMessage="Шинэчилж байна...">
                Нууц үг солих
              </Button>
            </form>
          </div>
        ) : (f.step == 2 && f.sentOTP) ? (
          <div className="mt-14">
            <div className="text-center flex flex-col items-center gap-2 max-w-xs mx-auto">
              <motion.div className="text-sm font-medium text-muted-foreground" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.5, delay: 0.2 }} viewport={{ once: true }}>
                Таны <span className="font-semibold text-foreground">{f.identifier}</span> руу илгээгдсэн 6 оронтой кодыг оруулна уу.
              </motion.div>
            </div>
            <div className="flex flex-col gap-4 mt-6">
              <motion.div className="flex justify-center" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.5, delay: 0.4 }} viewport={{ once: true }}>
                {/* @ts-ignore */}
                <InputOTP value={f.otp} onChange={f.setOTP} autoFocus={true} maxLength={6} pattern={REGEXP_ONLY_DIGITS} autoComplete="false">
                  {/* @ts-ignore */}
                  <InputOTPGroup>
                    {/* @ts-ignore */}
                    <InputOTPSlot index={0} showChar={true} />
                    {/* @ts-ignore */}
                    <InputOTPSlot index={1} showChar={true} />
                    {/* @ts-ignore */}
                    <InputOTPSlot index={2} showChar={true} />
                    {/* @ts-ignore */}
                    <InputOTPSlot index={3} showChar={true} />
                    {/* @ts-ignore */}
                    <InputOTPSlot index={4} showChar={true} />
                    {/* @ts-ignore */}
                    <InputOTPSlot index={5} showChar={true} />
                  </InputOTPGroup>
                </InputOTP>
              </motion.div>
              <motion.div className="flex flex-col gap-4" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.5, delay: 0.6 }} viewport={{ once: true }}>
                <Button type="button" onClick={f.handleOTP} icon={CircleCheckBig} isLoading={f.isOTPLoading} variant="outline" className="w-full h-10 bg-foreground text-white font-medium! border-foreground hover:bg-foreground/90 hover:text-white transition-all" loadingMessage="Баталгаажуулж байна...">
                  Баталгаажуулах
                </Button>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" onClick={f.reset} icon={MoveLeft} variant="ghost" className="w-full">
                    Буцах
                  </Button>
                  <Button type="button" onClick={f.resendOTP} icon={RotateCcw} isLoading={f.resendLoading} variant="ghost" className="w-full" loadingMessage="Илгээж байна..." disabled={f.isCooldownActive}>
                    {f.isCooldownActive ? `${f.cooldownSeconds} секунд` : 'Дахин илгээх'}
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        ) : (
          <div className="mt-14">
            <Tabs value={f.tab} onValueChange={(v: any) => f.setTab(v)} className="w-full">
              <TabsList className="w-full">
                <TabsTrigger value="phone">Утасны дугаар</TabsTrigger>
                <TabsTrigger value="email">Имэйл хаяг</TabsTrigger>
              </TabsList>
              <TabsContent value="phone">
                <div className="flex flex-col gap-y-4 mt-2">
                  <Input value={f.phone} label="Утасны дугаар" type="text" placeholder="00000000" onChange={(e: any) => f.setPhone(e.target.value)} onKeyDown={handleKeyDown} />
                  <Button onClick={f.sendOTP} isLoading={f.sendOTPLoading} icon={MoveRight} iconPosition="right" variant="outline" className="mt-2 w-full h-10 bg-foreground text-white font-medium! border-foreground hover:bg-foreground/90 hover:text-white transition-all" loadingMessage="Илгээж байна...">
                    Код илгээх
                  </Button>
                </div>
              </TabsContent>
              <TabsContent value="email">
                <div className="flex flex-col gap-y-4 mt-2">
                  <Input value={f.email} label="Имэйл хаяг" type="email" placeholder="example@shop.mn" onChange={(e: any) => f.setEmail(e.target.value)} onKeyDown={handleKeyDown} />
                  <Button onClick={f.sendOTP} isLoading={f.sendOTPLoading} icon={MoveRight} iconPosition="right" variant="outline" className="mt-2 w-full h-10 bg-foreground text-white font-medium! border-foreground hover:bg-foreground/90 hover:text-white transition-all" loadingMessage="Илгээж байна...">
                    Код илгээх
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="grow basis-0 border-b border-neutral-200" />
              <span className="text-sm font-medium leading-5 tracking-[-0.5%] text-muted-foreground">Эсвэл</span>
              <div className="grow basis-0 border-b border-neutral-200" />
            </div>
            <p className="mt-4 text-center text-foreground leading-5">
              Нууц үгээ сансан уу?{' '}
              <Link href={navigateUrl('/login', pathname)} className="cursor-pointer font-medium text-foreground underline hover:text-foreground/90 leading-5 tracking-[-0.5%] transition-colors">
                Нэвтрэх
              </Link>
            </p>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
